// Parses + minimally validates the JSON inside a ```stackiq-chart fence.
// The contract is defined by the backend system prompt
// (express/lib/ai/systemPrompt.js VISUALIZATION_INSTRUCTIONS) — keep in sync.

export type TableSpec = {
  type: 'table';
  title?: string;
  columns: { key: string; label: string }[];
  rows: Record<string, unknown>[];
};

export type SeriesSpec = {
  type: 'bar_chart' | 'stacked_bar_chart' | 'grouped_bar_chart' | 'line_chart' | 'histogram';
  title?: string;
  x_key: string;
  series: { key: string; label?: string }[];
  data: Record<string, unknown>[];
};

export type StatCardsSpec = {
  type: 'stat_cards';
  title?: string;
  cards: { label: string; value: number | string; unit?: string }[];
};

// scatter_plot: numeric x/y points. dot_chart: numeric x against category y.
export type PointsSpec = {
  type: 'scatter_plot' | 'dot_chart';
  title?: string;
  x_key: string;
  y_key: string;
  data: Record<string, unknown>[];
};

export type TimelineSpec = {
  type: 'timeline';
  title?: string;
  events: { date: string; label: string; description?: string }[];
};

export type ChartSpec = TableSpec | SeriesSpec | StatCardsSpec | PointsSpec | TimelineSpec;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const parseChartBlock = (raw: string): ChartSpec | null => {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isObject(parsed) || typeof parsed.type !== 'string') return null;

  switch (parsed.type) {
    case 'table': {
      const { columns, rows } = parsed as Partial<TableSpec>;
      if (!Array.isArray(columns) || !Array.isArray(rows)) return null;
      if (!columns.every((c) => isObject(c) && typeof c.key === 'string')) return null;
      if (!rows.every(isObject)) return null;
      return parsed as TableSpec;
    }
    case 'bar_chart':
    case 'stacked_bar_chart':
    case 'grouped_bar_chart':
    case 'line_chart':
    case 'histogram': {
      const { x_key, series, data } = parsed as Partial<SeriesSpec>;
      if (typeof x_key !== 'string' || !Array.isArray(series) || !Array.isArray(data)) return null;
      if (!series.every((s) => isObject(s) && typeof s.key === 'string')) return null;
      if (!data.every(isObject)) return null;
      return parsed as SeriesSpec;
    }
    case 'scatter_plot':
    case 'dot_chart': {
      const { x_key, y_key, data } = parsed as Partial<PointsSpec>;
      if (typeof x_key !== 'string' || typeof y_key !== 'string' || !Array.isArray(data)) return null;
      if (!data.every(isObject)) return null;
      return parsed as PointsSpec;
    }
    case 'timeline': {
      const { events } = parsed as Partial<TimelineSpec>;
      if (!Array.isArray(events)) return null;
      if (!events.every((e) => isObject(e) && typeof e.label === 'string' && typeof e.date === 'string'))
        return null;
      return parsed as TimelineSpec;
    }
    case 'stat_cards': {
      const { cards } = parsed as Partial<StatCardsSpec>;
      if (!Array.isArray(cards)) return null;
      if (!cards.every((c) => isObject(c) && typeof c.label === 'string' && 'value' in c)) return null;
      return parsed as StatCardsSpec;
    }
    default:
      return null;
  }
};
